import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEmployeeAuth } from './EmployeePortalLayout';
import {
  Send,
  Search,
  Globe,
  Volume2,
  Save,
  User,
  MessageCircle,
  Check,
  CheckCheck,
  X,
  ChevronDown,
  Languages,
  Mic,
  MicOff
} from 'lucide-react';
import { toast } from 'sonner';

const SPANISH_DIALECTS = [
  { code: 'es-MX', name: 'Mexican Spanish', country: 'Mexico', flag: '🇲🇽' },
  { code: 'es-ES', name: 'Castilian Spanish', country: 'Spain', flag: '🇪🇸' },
  { code: 'es-GT', name: 'Guatemalan Spanish', country: 'Guatemala', flag: '🇬🇹' },
  { code: 'es-SV', name: 'Salvadoran Spanish', country: 'El Salvador', flag: '🇸🇻' },
  { code: 'es-HN', name: 'Honduran Spanish', country: 'Honduras', flag: '🇭🇳' },
  { code: 'es-CO', name: 'Colombian Spanish', country: 'Colombia', flag: '🇨🇴' },
  { code: 'es-VE', name: 'Venezuelan Spanish', country: 'Venezuela', flag: '🇻🇪' }
];

const SPEECH_LANGUAGES = [
  { code: 'en-US', name: 'English', flag: '🇺🇸' },
  { code: 'es-MX', name: 'Spanish (Mexico)', flag: '🇲🇽' },
  { code: 'es-ES', name: 'Spanish (Spain)', flag: '🇪🇸' },
  { code: 'es-GT', name: 'Spanish (Guatemala)', flag: '🇬🇹' },
  { code: 'es-SV', name: 'Spanish (El Salvador)', flag: '🇸🇻' },
  { code: 'es-HN', name: 'Spanish (Honduras)', flag: '🇭🇳' },
  { code: 'es-CO', name: 'Spanish (Colombia)', flag: '🇨🇴' },
  { code: 'es-VE', name: 'Spanish (Venezuela)', flag: '🇻🇪' }
];

export const EmployeeMessages = () => {
  const { employee, api } = useEmployeeAuth();
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState({ employees: [], owners: [] });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [showContacts, setShowContacts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [selectedDialect, setSelectedDialect] = useState(SPANISH_DIALECTS[0]);
  const [showDialectPicker, setShowDialectPicker] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Voice-to-Text State
  const [isListening, setIsListening] = useState(false);
  const [speechLanguage, setSpeechLanguage] = useState(SPEECH_LANGUAGES[1]); // Default to Spanish for employees
  const [showSpeechLanguagePicker, setShowSpeechLanguagePicker] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      
      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setNewMessage(transcript);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied');
        }
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const toggleListening = () => {
    if (!speechSupported) {
      toast.error('Speech recognition not supported');
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.lang = speechLanguage.code;
      recognitionRef.current.start();
      setIsListening(true);
      toast.info(`Escuchando en ${speechLanguage.name}...`);
    }
  };

  useEffect(() => {
    fetchConversations();
    fetchContacts();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/api/messages/conversations/employee');
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      // Get owners for employees to contact
      const response = await api.get('/api/users');
      setContacts({ 
        employees: [], 
        owners: response.data.map(o => ({ ...o, type: 'owner' }))
      });
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await api.get(`/api/messages/conversation/${conversationId}/employee`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    const recipient = selectedConversation.participants.find(p => p.id !== employee.id);
    if (!recipient) return;

    try {
      await api.post('/api/messages/send/employee', {
        recipient_id: recipient.id,
        recipient_type: recipient.type,
        content: newMessage,
        translate_to: autoTranslate ? 'en' : null, // Employees translate to English
        dialect: null
      });
      setNewMessage('');
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleStartConversation = async (contact) => {
    const existing = conversations.find(c => 
      c.participants.some(p => p.id === contact.id)
    );

    if (existing) {
      setSelectedConversation(existing);
    } else {
      setSelectedConversation({
        id: 'new',
        participants: [
          { id: employee.id, name: employee.name, type: 'employee' },
          { id: contact.id, name: contact.name, type: contact.type }
        ]
      });
      setMessages([]);
    }
    setShowContacts(false);
  };

  const handleTranslateMessage = async (message) => {
    try {
      // Employees translate messages to Spanish
      const response = await api.post(`/api/messages/message/${message.id}/translate?target_language=es&dialect=${selectedDialect.code}`);
      setMessages(prev => prev.map(m => 
        m.id === message.id 
          ? { ...m, translated_content: response.data.translated_text, dialect: response.data.dialect }
          : m
      ));
      toast.success('Mensaje traducido');
    } catch (error) {
      toast.error('Error al traducir');
    }
  };

  const speakText = (text, lang = 'es') => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      const voices = speechSynthesis.getVoices();
      const dialectVoice = voices.find(v => v.lang.startsWith(selectedDialect.code.split('-')[0]));
      if (dialectVoice) {
        utterance.voice = dialectVoice;
      }
      speechSynthesis.speak(utterance);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-160px)] flex flex-col bg-white rounded-2xl overflow-hidden" data-testid="employee-messages">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <h1 className="text-xl font-bold">Mensajes</h1>
        <button
          onClick={() => setShowContacts(true)}
          className="p-2 bg-[#C6A87C] text-white rounded-full"
        >
          <MessageCircle className="w-5 h-5" />
        </button>
      </div>

      {selectedConversation ? (
        <>
          {/* Chat Header */}
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setSelectedConversation(null)} className="p-1">
                <X className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                <User className="w-5 h-5 text-[#C6A87C]" />
              </div>
              <div>
                <p className="font-medium text-sm">
                  {selectedConversation.participants.find(p => p.id !== employee?.id)?.name}
                </p>
                <p className="text-xs text-gray-500">
                  {selectedConversation.participants.find(p => p.id !== employee?.id)?.type === 'owner' ? 'Jefe' : 'Compañero'}
                </p>
              </div>
            </div>

            {/* Dialect Picker */}
            <div className="relative">
              <button
                onClick={() => setShowDialectPicker(!showDialectPicker)}
                className="flex items-center gap-1 px-2 py-1 border border-gray-200 rounded text-xs"
              >
                <span>{selectedDialect.flag}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              
              <AnimatePresence>
                {showDialectPicker && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                  >
                    {SPANISH_DIALECTS.map((dialect) => (
                      <button
                        key={dialect.code}
                        onClick={() => {
                          setSelectedDialect(dialect);
                          setShowDialectPicker(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${
                          selectedDialect.code === dialect.code ? 'bg-[#C6A87C]/10' : ''
                        }`}
                      >
                        <span>{dialect.flag}</span>
                        <span>{dialect.country}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((message) => {
              const isOwn = message.sender_id === employee?.id;
              return (
                <div
                  key={message.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%]`}>
                    <div
                      className={`p-3 rounded-2xl text-sm ${
                        isOwn 
                          ? 'bg-[#C6A87C] text-white rounded-br-sm' 
                          : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                      }`}
                    >
                      <p>{message.content}</p>
                      {message.translated_content && (
                        <div className={`mt-2 pt-2 border-t ${isOwn ? 'border-white/20' : 'border-gray-200'}`}>
                          <p className="italic">{message.translated_content}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className={`flex items-center gap-2 mt-1 text-xs text-gray-400 ${isOwn ? 'justify-end' : ''}`}>
                      <span>{new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      {!isOwn && (
                        <div className="flex gap-1">
                          {!message.translated_content && (
                            <button onClick={() => handleTranslateMessage(message)} className="p-1 hover:text-[#C6A87C]">
                              <Globe className="w-3 h-3" />
                            </button>
                          )}
                          {message.translated_content && (
                            <button onClick={() => speakText(message.translated_content)} className="p-1 hover:text-[#C6A87C]">
                              <Volume2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-100">
            {speechSupported && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">Hablar en:</span>
                <button
                  type="button"
                  onClick={() => setShowSpeechLanguagePicker(!showSpeechLanguagePicker)}
                  className="flex items-center gap-1 px-2 py-1 border border-gray-200 rounded text-xs"
                >
                  <span>{speechLanguage.flag}</span>
                  <span>{speechLanguage.name}</span>
                </button>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              {speechSupported && (
                <button
                  type="button"
                  onClick={toggleListening}
                  data-testid="employee-voice-to-text-btn"
                  className={`p-3 rounded-full ${
                    isListening 
                      ? 'bg-red-500 text-white animate-pulse' 
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>
              )}
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={isListening ? 'Escuchando...' : 'Escribe un mensaje...'}
                className={`flex-1 px-4 py-3 border rounded-full text-sm ${
                  isListening ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
              />
              <button
                type="submit"
                disabled={!newMessage.trim()}
                className="p-3 bg-[#C6A87C] text-white rounded-full disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </form>
        </>
      ) : (
        /* Conversation List */
        <div className="flex-1 overflow-y-auto">
          {conversations.length > 0 ? (
            conversations.map((conv) => {
              const otherParticipant = conv.participants.find(p => p.id !== employee?.id);
              return (
                <div
                  key={conv.id}
                  onClick={() => setSelectedConversation(conv)}
                  className="p-4 border-b border-gray-50 active:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-[#C6A87C]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{otherParticipant?.name}</p>
                        {conv.unread_count > 0 && (
                          <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{conv.last_message}</p>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-8 text-center text-gray-500">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p>No hay conversaciones</p>
              <button
                onClick={() => setShowContacts(true)}
                className="mt-2 text-[#C6A87C] text-sm"
              >
                Iniciar conversación
              </button>
            </div>
          )}
        </div>
      )}

      {/* Contacts Modal */}
      <AnimatePresence>
        {showContacts && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end z-50"
            onClick={() => setShowContacts(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              className="bg-white w-full rounded-t-2xl max-h-[70vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-center">Contactar</h3>
              </div>
              <div className="p-4 max-h-96 overflow-y-auto">
                <p className="text-xs text-gray-500 uppercase mb-2">Jefes</p>
                {contacts.owners.map((owner) => (
                  <button
                    key={owner.id}
                    onClick={() => handleStartConversation(owner)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#C6A87C]" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">{owner.name}</p>
                      <p className="text-xs text-gray-500">{owner.title || 'Owner'}</p>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmployeeMessages;
